/**
 * AIImportConfirmStep
 * Final step after Apply succeeds. Shows what was created, a link to the
 * new deal, and "Start another import" / rollback affordances.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, RotateCcw, Sparkles, Database, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAIImportJob, useRollbackAIImportJob } from '@/hooks/useAIImport';
import { useAuthStore } from '@/stores/authStore';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { useUIStore } from '@/stores/uiStore';

interface AIImportConfirmStepProps {
  jobId: string;
  onClose: () => void;
  onStartAnother: () => void;
}

export function AIImportConfirmStep({ jobId, onClose, onStartAnother }: AIImportConfirmStepProps) {
  const { data: job } = useAIImportJob(jobId);
  const rollback = useRollbackAIImportJob();
  const { user, profile } = useAuthStore();
  const { user: msUser } = useMicrosoftAuth();
  const addToast = useUIStore((s) => s.addToast);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const currentUserName =
    profile?.full_name || msUser?.name || user?.email || msUser?.email || 'Unknown';
  const currentEmail = user?.email || msUser?.email || null;

  if (!job) return null;

  const isDatabaseMode = job.summary?.import_mode === 'database';

  const handleRollback = async () => {
    try {
      await rollback.mutateAsync({ id: jobId, rolled_back_by: currentUserName, rolled_back_by_email: currentEmail });
      setShowRollbackConfirm(false);
      onClose();
    } catch (err) {
      setShowRollbackConfirm(false);
      addToast({ type: 'error', title: 'Rollback failed', message: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="text-center py-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center dark:bg-green-900/30">
          <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="mt-3 text-xl font-semibold text-gray-900 dark:text-gray-100">
          {isDatabaseMode ? 'Added to your database' : 'Import applied'}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {isDatabaseMode ? (
            <>
              {job.properties_created} propert{job.properties_created === 1 ? 'y' : 'ies'} created,{' '}
              {job.properties_updated} updated, with sales comps and market rents added to Research.
            </>
          ) : (
            <>
              {job.properties_created} propert{job.properties_created === 1 ? 'y was' : 'ies were'}{' '}
              created, {job.properties_updated} updated, and a new deal with a base financial model
              was added.
            </>
          )}
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 p-4 space-y-3 dark:border-gray-700">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider dark:text-gray-400">Properties</div>
            <div className="text-lg font-semibold text-gray-900 mt-0.5 dark:text-gray-100">
              {job.properties_created + job.properties_updated}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider dark:text-gray-400">Documents</div>
            <div className="text-lg font-semibold text-gray-900 mt-0.5 dark:text-gray-100">{job.files_count}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider dark:text-gray-400">Comps</div>
            <div className="text-lg font-semibold text-gray-900 mt-0.5 dark:text-gray-100">
              {job.valuations_created}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {job.created_deal_id && (
          <Link
            to={`/deals/${job.created_deal_id}`}
            onClick={onClose}
            className="flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 hover:bg-primary-100 transition-colors dark:border-primary-800 dark:bg-primary-900/30 dark:hover:bg-primary-900/40"
          >
            <div>
              <div className="font-medium text-primary-900 dark:text-primary-300">
                {job.summary?.deal?.name ?? 'Open new deal'}
              </div>
              <div className="text-xs text-primary-700 dark:text-primary-300">
                View the deal and its base financial model in Flow
              </div>
            </div>
            <ExternalLink className="h-4 w-4 text-primary-700 dark:text-primary-300" />
          </Link>
        )}

        {isDatabaseMode && (
          <>
            <Link
              to="/property-lists"
              onClick={onClose}
              className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 hover:bg-emerald-100 transition-colors dark:border-emerald-800 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/40"
            >
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
                <div>
                  <div className="font-medium text-emerald-900 dark:text-emerald-300">View Property Lists</div>
                  <div className="text-xs text-emerald-700 dark:text-emerald-300">Properties added/updated by this import</div>
                </div>
              </div>
              <ExternalLink className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
            </Link>
            <Link
              to="/research"
              onClick={onClose}
              className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50 transition-colors dark:border-gray-700 dark:hover:bg-gray-800"
            >
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">Open Research</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Sales/refi comps and market rents captured</div>
                </div>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </Link>
          </>
        )}

        <Link
          to={`/ai-imports/${jobId}`}
          onClick={onClose}
          className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50 transition-colors dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">View import details</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Full job summary, files, and questions</div>
          </div>
          <ExternalLink className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        </Link>
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          onClick={() => setShowRollbackConfirm(true)}
          disabled={rollback.isPending}
          leftIcon={<RotateCcw className="h-4 w-4" />}
          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
        >
          Roll back
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button
            onClick={onStartAnother}
            leftIcon={<Sparkles className="h-4 w-4" />}
          >
            Start another import
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={showRollbackConfirm}
        onOpenChange={setShowRollbackConfirm}
        title="Roll back import?"
        description={
          isDatabaseMode
            ? 'Roll back this update? Properties created by this import and the comps/rents it added will be removed.'
            : 'Roll back this import? Created properties and the deal will be soft-deleted.'
        }
        confirmLabel="Roll back"
        variant="danger"
        loading={rollback.isPending}
        onConfirm={handleRollback}
      />
    </div>
  );
}
